/**
 * The shared storage-browser ROW-menu registry (S8 Task 8.10; owned by S8 — ruling D13). Slices register items that
 * render in ONE slot of `StorageBrowserRowMenu`, directly after "Get SAS…" and before "Delete", ordered by
 * `(order, id)` (bands: S8 800–899; later slices take later bands). An item whose `isVisible` answers false is ABSENT
 * (never a placeholder for a capability the account lacks); one whose `disabledReason` answers a string renders
 * disabled with that string as its `title`. There is no override behaviour: an extension can never replace, hide or
 * re-target a built-in item. After the S7 merge the context gains `capabilities?` (S7's `rowMenuCapabilities` answer —
 * merge hunk H7).
 */
import type { ComponentType } from 'react';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import type { StorageExplorerMode } from '../../core/types/workspaceTypes';
import type { StorageDialogRequest } from '../properties/dialogs/storageDialogStore';
import type { StorageBrowserRow } from '../utils/storageItemRows';

export type StorageRowMenuContext = {
  connectionId: string;
  mode: StorageExplorerMode;
  root: StorageResourceRef;
  location: StorageResourceRef;
  readOnly: boolean;
  hns: 'yes' | 'no' | 'unknown';
  /** The S3 dialog-store opener (`storageDialogStore.tsx`). */
  openDialog: (request: StorageDialogRequest) => void;
};

export type StorageRowMenuSubItem = { id: string; label: string; testId: string; onSelect(row: StorageBrowserRow, ctx: StorageRowMenuContext): void };
export type StorageRowMenuIcon = ComponentType<{ className?: string }>;

export type StorageRowMenuItem = {
  /** Unique `<slice>-<action>`, e.g. `s8-rename`; a duplicate id throws at registration. */
  id: string;
  /** Bands: S8 800–899; ties broken by id. */
  order: number;
  /** `storage-row-menu-<id>`. */
  testId: string;
  label: string | ((row: StorageBrowserRow, ctx: StorageRowMenuContext) => string);
  /** False = ABSENT. */
  isVisible(row: StorageBrowserRow, ctx: StorageRowMenuContext): boolean;
  /** A string = rendered disabled with it as `title`. */
  disabledReason?(row: StorageBrowserRow, ctx: StorageRowMenuContext): string | undefined;
  onSelect?(row: StorageBrowserRow, ctx: StorageRowMenuContext): void;
  submenu?: StorageRowMenuSubItem[];
  /** UI/UX fix round A (U16): the item's icon and the grid key bound to it, as the built-in items show them. */
  icon?: StorageRowMenuIcon;
  shortcut?: string;
  /**
   * `copy`: the item's submenu entries join the row menu's ONE "Copy" submenu, after its URL / path (review U16: one
   * Copy group, no second "Copy path as" repeating them) — the item itself renders no entry.
   */
  group?: 'copy';
};

const items = new Map<string, StorageRowMenuItem>();

export function registerStorageRowMenuExtension(item: StorageRowMenuItem): () => void {
  if (items.has(item.id)) throw new Error(`A storage row-menu item with id "${item.id}" is already registered.`);
  items.set(item.id, item);
  return () => {
    if (items.get(item.id) === item) items.delete(item.id);
  };
}

/** The visible items for `row`, sorted by `(order, id)`. */
export function storageRowMenuItems(row: StorageBrowserRow, ctx: StorageRowMenuContext): StorageRowMenuItem[] {
  return [...items.values()]
    .filter((item) => item.isVisible(row, ctx))
    .sort((left, right) => left.order - right.order || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}

export const storageRowMenuLabel = (item: StorageRowMenuItem, row: StorageBrowserRow, ctx: StorageRowMenuContext): string =>
  typeof item.label === 'function' ? item.label(row, ctx) : item.label;

export function clearStorageRowMenuExtensionsForTests(): void {
  items.clear();
}
